import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { toast } from "react-toastify";
import { Box, Typography, CircularProgress, Alert, useTheme, useMediaQuery } from "@mui/material";
import { getTimeEntryData } from "src/services/timeManagementService";
import ClocknIcon from "@mui/icons-material/AccessTimeOutlined";

const dynamicColors = [
   "#00AEEF", // Bright Blue (Brand)
  "#232C68", // Deep Navy Blue (Brand)
  "#F05423", // Vibrant Orange (Brand)
  "#58595B", // Charcoal Gray (Brand)
  "#E6F7FB", // Light Blue Tint (Accent)
  "#1A2043", // Rich Navy (Accent)
  "#FAE1D3", // Soft Peach (Accent)
  "#A7A8AA", // Light Gray (Accent)
  "#FFD166", // Soft Gold (Accent)
  "#00698F", // Muted Teal (Accent)
];

interface TimeEntryChartProps {
  appUser: { EmployeeId: string };
  selectedStatus: string[];
}

const TimeEntryChart: React.FC<TimeEntryChartProps> = ({
  appUser,
  selectedStatus
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [timeEntries, setTimeEntries] = useState<
    { project: string; timePerWeek: number[] }[]
  >([]);
  const [weekRanges, setWeekRanges] = useState<
    { start: string; end: string }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to fetch data for three weeks
  const fetchTimeEntryDataForThreeWeeks = async (weekStartDates: Date[]) => {
    const promises = weekStartDates.map((weekStartDate) => {
      const payload = {
        employeeId: appUser.EmployeeId,
        startDate: formatDate(weekStartDate),
        endDate: formatDate(
          new Date(weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000)
        ), // Add 6 days for week end
        filter:
          selectedStatus && selectedStatus.length > 0 ? selectedStatus : null // Add null check for selectedStatus
      };
      return getTimeEntryData(payload);
    });

    try {
      const responses = await Promise.all(promises);
      return responses.map((response) => {
        if (response.data.statusCode === 200) {
          const data = response.data.data;

          // Transform daysOfWeeks for each project/task
          const transformDaysOfWeeks = (daysOfWeeks: { hoursWorked: string | null; day: string }[]) =>
            daysOfWeeks.map((day) => ({
              ...day,
              hoursWorked: day.hoursWorked ?? "0h 0m",
            }));

          data.projects = data.projects?.map((project: any) => ({
            ...project,
            tasks: project.tasks.map((task: any) => ({
              ...task,
              daysOfWeeks: transformDaysOfWeeks(task.daysOfWeeks),
            })),
          }));

          return data;
        }
        throw new Error("Failed to fetch time entry data");
      });
    } catch (error) {
      throw error;
    }
  };

  // Helper function to calculate project-wise weekly totals
  const calculateThreeWeeksProjectData = (timeEntryData: any[]) => {
    const projectWeeklyTotals: Record<string, number[]> = {};

    timeEntryData.forEach((weekData, weekIndex) => {
      const projectTotals = weekData.projects.map((project: any) => {
        const dayOnProjectTotalHours: Record<string, { hours: number; minutes: number }> = {};

        project.tasks.forEach((task: any) => {
          task.daysOfWeeks.forEach((entry: { hoursWorked: string; day: string }) => {
              if (entry.hoursWorked) {
                const [hours, minutes] = entry.hoursWorked
                .split("h")
                .map((part) => parseInt(part.replace("m", "").trim()));

                const lowerCaseDay = entry.day.toLowerCase();

                if (!dayOnProjectTotalHours[lowerCaseDay]) {
                dayOnProjectTotalHours[lowerCaseDay] = { hours: 0, minutes: 0 };
                }

                dayOnProjectTotalHours[lowerCaseDay].hours += hours;
                dayOnProjectTotalHours[lowerCaseDay].minutes += minutes;

                if (dayOnProjectTotalHours[lowerCaseDay].minutes >= 60) {
                  dayOnProjectTotalHours[lowerCaseDay].hours += Math.floor(
                    dayOnProjectTotalHours[lowerCaseDay].minutes / 60
                  );
                  dayOnProjectTotalHours[lowerCaseDay].minutes %= 60;
                }
              }
            }
          );
        });

        const weeklyTotal = Object.values(dayOnProjectTotalHours).reduce(
          (acc, { hours, minutes }) => {
            acc.hours += hours;
            acc.minutes += minutes;

            if (acc.minutes >= 60) {
              acc.hours += Math.floor(acc.minutes / 60);
              acc.minutes %= 60;
            }

            return acc;
          },
          { hours: 0, minutes: 0 }
        );

        return {
          projectName: project.projectName, // Use projectName instead of projectId
          totalHours: weeklyTotal.hours + weeklyTotal.minutes / 60, // Weekly total in decimal format
        };
      });

      projectTotals.forEach((project) => {
        if (!projectWeeklyTotals[project.projectName]) {
          projectWeeklyTotals[project.projectName] = Array(timeEntryData.length).fill(0);
        }

        projectWeeklyTotals[project.projectName][weekIndex] = project.totalHours;
      });
    });

    return Object.entries(projectWeeklyTotals).map(([projectName, timePerWeek]) => ({
        project: projectName,
      timePerWeek,
    }));
  };

  const formatDate = (date: Date) =>
    date.toISOString().split("T")[0];

  useEffect(() => {
    const today = new Date();
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay() + 1);

    const weekStartDates = [
      new Date(currentWeekStart.setDate(currentWeekStart.getDate() - 14)),
      new Date(currentWeekStart.setDate(currentWeekStart.getDate() + 7)),
      new Date(currentWeekStart.setDate(currentWeekStart.getDate() + 7))
    ];

    const weekRanges = weekStartDates.map((startDate) => {
      const endDate = new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000);
      const format = (date: Date) =>
        date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      return { start: format(startDate), end: format(endDate) };
    });

    setWeekRanges(weekRanges);
    setLoading(true);
    setError(null);

    fetchTimeEntryDataForThreeWeeks(weekStartDates)
      .then((data) => {
        const processedData = calculateThreeWeeksProjectData(data);
        setTimeEntries(processedData);
      })
      .catch((error) => {
        toast.error('Error fetching time entry data. Please try again later.');

        setError('Failed to load time entry data. Please try again later.');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUser, selectedStatus]);

  const labels = weekRanges.map((range) => `${range.start} - ${range.end}`);

  const datasets = timeEntries.map((entry, index) => ({
    label: entry.project,
    data: entry.timePerWeek,
    backgroundColor: dynamicColors[index % dynamicColors.length],
    barThickness: 50
  }));

  const data = { labels, datasets };

  const getStepSize = (max: number) => {
    // Start at 2, double until it's roughly 8 or fewer steps
    let step = 2;
    while (max / step > 8) {
      step *= 2;
    }
    return step;
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    tooltips: {
      callbacks: {
        label: (tooltipItem: any, data: any) => {
          const raw = tooltipItem.yLabel;
          if (raw === 0) return '';
          const hours = Math.floor(raw);
          const minutes = Math.round((raw - hours) * 60);
          return `${
            data.datasets[tooltipItem.datasetIndex].label
          }: ${hours}h ${minutes}m`;
        }
      }
    },
    legend: {
      position: 'bottom',
      labels: {
        fontColor: theme.palette.text.primary,
        fontWeight: 'bold',
      }
    },
    scales: {
      xAxes: [
        {
          stacked: true,
          scaleLabel: {
            display: true,
            labelString: 'Weeks',
            fontStyle: 'bold',
            fontColor: theme.palette.text.primary
          },
          ticks: {
            fontColor: theme.palette.text.primary,
            padding: isMobile ? 5 : 10
          },
          gridLines: {
            display: true,
            color: theme.palette.divider,
            lineWidth: 0.5,
            drawTicks: false
          }
        }
      ],
      yAxes: [
        {
          stacked: true,
          scaleLabel: {
            display: true,
            labelString: 'Hours Worked',
            fontStyle: 'bold',
            fontColor: theme.palette.text.primary
          },
          ticks: {
            beginAtZero: true,
            // stepSize will be set dynamically in 'afterBuildTicks'
            callback: (value: number) => `${value}h`,
            fontColor: theme.palette.text.primary
          },
          // Dynamically update stepSize
          afterBuildTicks: function (axis) {
            const max = axis.max;
            axis.options.ticks.stepSize = getStepSize(max);
          }
        }
      ]
    }
  };

  return (
    <Box
      sx={{
        padding: isMobile ? '1rem' : '1.5rem',
        background: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '6px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}
    >
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="320px"
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ textAlign: 'center' }}>
          {error}
        </Alert>
      ) : (
        <>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            sx={{ marginBottom: '1.5rem' }}
          >
            <Box display="flex" alignItems="center">
                <ClocknIcon
                  sx={{
                    alignSelf: "center",
                    fontSize: "20px",
                    marginRight: "5px",
                  }}
                />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  display: 'inline-block',
                  textAlign: 'left'
                }}
              >
                 Time Entry
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  marginLeft: 1,
                  fontSize: '12px',
                  color: '#666',
                  fontWeight: 400
                }}
              >
                (Last three weeks)
              </Typography>
            </Box>

            {weekRanges.length > 0 && (
              <Typography variant="caption" color="textSecondary">
                {weekRanges[0].start} - {weekRanges[2].end}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              height: '380px',
              width: '100%'
            }}
          >
            <Bar data={data} options={options} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default TimeEntryChart;